/**
 * Weather Suitability Utility for HoneyDo Platform
 * Determines optimal weather conditions for different task categories
 */

export interface WeatherConditions {
  conditions: 'sunny' | 'rainy' | 'cloudy' | 'snow' | 'windy' | 'humid' | 'hot' | 'cold';
  tempHigh: number;
  tempLow: number;
  rainChance: number;
  humidity?: number;
  windSpeed?: number;
  uvIndex?: number;
}

export interface SuitabilityResult {
  score: number; // 0-100
  level: 'excellent' | 'good' | 'fair' | 'poor';
  reasoning: string;
  icon: string;
  shouldSuggest: boolean;
}

export interface TaskSuggestion {
  category: string;
  title: string;
  suitability: SuitabilityResult;
  tasks?: any[]; // Tasks from the system matching this category
}

/**
 * Task category weather requirements
 */
const CATEGORY_REQUIREMENTS = {
  landscaping: {
    tempRange: [60, 80],
    maxRain: 30,
    maxWind: 15,
    maxHumidity: 80,
    description: 'Landscaping & Yard Work'
  },
  painting_exterior: {
    tempRange: [50, 85],
    maxRain: 20,
    maxWind: 12,
    maxHumidity: 70,
    description: 'Exterior Painting'
  },
  general_outdoor: {
    tempRange: [50, 85],
    maxRain: 40,
    maxWind: 20,
    description: 'General Outdoor Tasks'
  },
  home_maintenance: {
    tempRange: [45, 90],
    maxRain: 60,
    description: 'Home Maintenance'
  },
  roof_work: {
    tempRange: [55, 85],
    maxRain: 10,
    maxWind: 10,
    description: 'Roof Work & Repairs'
  },
  concrete_work: {
    tempRange: [50, 85],
    maxRain: 20,
    maxHumidity: 75,
    description: 'Concrete & Masonry'
  },
  pest_control: {
    tempRange: [60, 90],
    maxRain: 30,
    maxWind: 15,
    description: 'Pest Control Treatment'
  },
  window_cleaning: {
    tempRange: [50, 80],
    maxRain: 30,
    maxWind: 15,
    description: 'Window & Exterior Cleaning'
  }
};

/**
 * Calculate weather suitability for a task category
 */
export function calculateSuitability(
  category: string,
  weather: WeatherConditions
): SuitabilityResult {
  // Default to general outdoor if category not found
  const categoryKey = Object.keys(CATEGORY_REQUIREMENTS).find(key =>
    category.toLowerCase().includes(key.replace('_', ' '))
  ) || 'general_outdoor';

  const requirements = CATEGORY_REQUIREMENTS[categoryKey as keyof typeof CATEGORY_REQUIREMENTS];
  let score = 100;
  const issues: string[] = [];

  // Temperature check - use tempHigh as current temp indicator
  const [minTemp, maxTemp] = requirements.tempRange;
  const currentTemp = weather.tempHigh;

  if (currentTemp < minTemp) {
    const deficit = minTemp - currentTemp;
    // Penalize more severely for being out of range
    score -= Math.min(50, deficit * 3);
    issues.push(`Temperature too cold (${currentTemp}°F, need ${minTemp}°F+)`);
  } else if (currentTemp > maxTemp) {
    const excess = currentTemp - maxTemp;
    score -= Math.min(50, excess * 3);
    issues.push(`Temperature too hot (${currentTemp}°F, max ${maxTemp}°F)`);
  }

  // Rain check
  if (requirements.maxRain && weather.rainChance > requirements.maxRain) {
    const excess = weather.rainChance - requirements.maxRain;
    score -= Math.min(40, excess * 0.8);
    issues.push(`${weather.rainChance}% rain chance`);
  }

  // Wind check
  if ('maxWind' in requirements && requirements.maxWind && weather.windSpeed && weather.windSpeed > requirements.maxWind) {
    const excess = weather.windSpeed - requirements.maxWind;
    score -= Math.min(20, excess * 1.5);
    issues.push(`High winds (${weather.windSpeed}mph)`);
  }

  // Humidity check
  if ('maxHumidity' in requirements && requirements.maxHumidity && weather.humidity && weather.humidity > requirements.maxHumidity) {
    const excess = weather.humidity - requirements.maxHumidity;
    score -= Math.min(15, excess * 0.3);
    issues.push(`High humidity (${weather.humidity}%)`);
  }

  // Conditions check
  if (weather.conditions === 'rainy' || weather.conditions === 'snow') {
    score -= 30;
    issues.push(`${weather.conditions} conditions`);
  }

  // Ensure score is in valid range
  score = Math.max(0, Math.min(100, score));

  // Determine level
  let level: SuitabilityResult['level'];
  if (score >= 85) level = 'excellent';
  else if (score >= 65) level = 'good';
  else if (score >= 45) level = 'fair';
  else level = 'poor';

  // Generate reasoning
  let reasoning: string;
  if (score >= 85) {
    reasoning = `Perfect conditions! ${weather.tempHigh}°F with low humidity and minimal rain chance.`;
  } else if (score >= 65) {
    reasoning = `Good conditions overall. ${issues.length > 0 ? issues.slice(0, 1).join(', ') : 'Suitable weather'}`;
  } else if (score >= 45) {
    reasoning = `Manageable but not ideal. ${issues.slice(0, 2).join(', ')}`;
  } else {
    reasoning = `Not recommended: ${issues.slice(0, 2).join(', ')}`;
  }

  // Select icon
  const icon = level === 'excellent' ? '☀️' :
               level === 'good' ? '⛅' :
               level === 'fair' ? '⚠️' : '⛈️';

  return {
    score,
    level,
    reasoning,
    icon,
    shouldSuggest: score >= 75 && issues.length === 0 // Only suggest if very good with no warnings
  };
}

/**
 * Analyze weather across next N days and find best opportunities
 */
export function findBestWeatherDays(
  forecast: WeatherConditions[],
  category: string,
  maxDays: number = 3
): Array<{ date: string; suitability: SuitabilityResult }> {
  return forecast
    .slice(0, maxDays)
    .map((weather, index) => ({
      date: new Date(Date.now() + index * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }),
      suitability: calculateSuitability(category, weather)
    }))
    .filter(day => day.suitability.shouldSuggest)
    .sort((a, b) => b.suitability.score - a.suitability.score);
}

/**
 * Get task suggestions based on current and forecast weather
 */
export function getWeatherTaskSuggestions(
  currentWeather: WeatherConditions,
  forecast: WeatherConditions[],
  userTasks: any[]
): TaskSuggestion[] {
  const suggestions: TaskSuggestion[] = [];

  // Categories to check
  const categoriesToCheck = [
    'landscaping',
    'painting_exterior',
    'general_outdoor',
    'home_maintenance',
    'roof_work',
    'window_cleaning'
  ];

  for (const category of categoriesToCheck) {
    const suitability = calculateSuitability(category, currentWeather);

    // Only suggest if conditions are good or better
    if (suitability.shouldSuggest) {
      // Find matching tasks
      const matchingTasks = userTasks.filter(task => {
        const taskCategory = task.category?.toLowerCase() || '';
        const categoryNormalized = category.replace('_', ' ').toLowerCase();
        return taskCategory.includes(categoryNormalized) ||
               categoryNormalized.includes(taskCategory);
      });

      suggestions.push({
        category,
        title: CATEGORY_REQUIREMENTS[category as keyof typeof CATEGORY_REQUIREMENTS].description,
        suitability,
        tasks: matchingTasks
      });
    }
  }

  // Sort by suitability score
  return suggestions.sort((a, b) => b.suitability.score - a.suitability.score);
}

/**
 * Get warning about upcoming bad weather for pending outdoor tasks
 */
export function getWeatherWarnings(
  forecast: WeatherConditions[],
  pendingOutdoorTasks: any[]
): Array<{ message: string; severity: 'warning' | 'info'; tasks: any[] }> {
  const warnings: Array<{ message: string; severity: 'warning' | 'info'; tasks: any[] }> = [];

  if (pendingOutdoorTasks.length === 0) return warnings;

  // Check next 3 days for bad weather
  const next3Days = forecast.slice(0, 3);
  const badWeatherDays = next3Days.filter(weather =>
    weather.rainChance > 60 ||
    weather.conditions === 'rainy' ||
    weather.conditions === 'snow' ||
    weather.tempHigh > 95 ||
    weather.tempLow < 32
  );

  if (badWeatherDays.length > 0) {
    const outdoorTasks = pendingOutdoorTasks.filter(task => {
      const category = task.category?.toLowerCase() || '';
      return category.includes('outdoor') ||
             category.includes('landscaping') ||
             category.includes('painting') ||
             category.includes('roof');
    });

    if (outdoorTasks.length > 0) {
      warnings.push({
        message: `Bad weather expected in next ${badWeatherDays.length} day${badWeatherDays.length > 1 ? 's' : ''}. Consider completing outdoor tasks today!`,
        severity: 'warning',
        tasks: outdoorTasks
      });
    }
  }

  return warnings;
}

/**
 * Find next good weather day in forecast
 */
export function findNextGoodDay(
  forecast: WeatherConditions[],
  category: string
): { day: string; suitability: SuitabilityResult } | null {
  for (let i = 0; i < forecast.length; i++) {
    const weather = forecast[i];
    const suitability = calculateSuitability(category, weather);

    if (suitability.shouldSuggest) {
      const date = new Date(Date.now() + i * 24 * 60 * 60 * 1000);
      return {
        day: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        suitability
      };
    }
  }

  return null;
}

/**
 * Check if indoor tasks should be suggested (bad outdoor weather)
 */
export function shouldSuggestIndoorTasks(weather: WeatherConditions): boolean {
  return (
    weather.rainChance > 60 ||
    weather.conditions === 'rainy' ||
    weather.conditions === 'snow' ||
    weather.tempHigh > 95 ||
    weather.tempHigh < 45  // Too cold for most outdoor work
  );
}
